import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IsLoadingDataSpotlight,
  LastSpotlight,
  SpotlightHistory,
  loadSpotlightData,
  SpotlightStatuses,
  IsSpotlightOn,
  GetActualProductName,
  loadSpotlightStatuses,
  SelectActualProductId,
  SelectNeedRefreshSpotlight,
  setIsLoadingDataSpotlight,
  IsProductActive,
  IsContentEditable,
} from "../redux/reducers/spotlight.reducer";
import { SpotlightService } from "./../services/spotlight";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { ROUTE_DELETE_PRODUCT_PAGE } from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";

export const useSpotlights = (productId: number) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const actualProductName = useSelector(GetActualProductName);
  const actualProductId = useSelector(SelectActualProductId);
  const isLoadingDataSpotlight = useSelector(IsLoadingDataSpotlight);
  const isSpotlightOn = useSelector(IsSpotlightOn);
  const isProductActive = useSelector(IsProductActive);
  const isContentEditable = useSelector(IsContentEditable);
  const lastSpotlight = useSelector(LastSpotlight);
  const spotlightHistory = useSelector(SpotlightHistory);
  const spotlightStatuses = useSelector(SpotlightStatuses);
  const needRefreshSpotlight = useSelector(SelectNeedRefreshSpotlight);

  const { rolesLoaded } = useSelector(selectUserRoles);

  const loadSpotlightInfo = useCallback(async () => {
    if (rolesLoaded) {
      if (spotlightStatuses.length === 0) {
        const spotlightStatusesResponse =
          await SpotlightService.getSpotlightStatuses()
            .then((data) => {
              return data;
            })
            .catch((error) => {
              return error.response || error.message;
            });
        spotlightStatusesResponse.sort(
          (a: any, b: any) => a.sortOrder - b.sortOrder
        );
        dispatch(
          loadSpotlightStatuses({
            spotlightStatuses: spotlightStatusesResponse,
          })
        );
      }

      if (
        !actualProductId ||
        (actualProductId && actualProductId !== productId) ||
        needRefreshSpotlight
      ) {
        try {
          const [lastSpotlightResponse, spotlightHistoryResponse] =
            await Promise.all([
              SpotlightService.getLastSpotlight(productId),
              SpotlightService.getSpotlightHistory(productId),
            ]);

          let action: any = {
            productId: productId,
            lastSpotlightInfo: lastSpotlightResponse.data,
            spotlightHistory: spotlightHistoryResponse,
          };
          dispatch(loadSpotlightData(action));
        } catch (error: any) {
          dispatch(setIsLoadingDataSpotlight(false));
          dispatch(
            addToast({
              mode: "error",
              message: error?.message
                ? `Error: ${error.message}`
                : "Something wrong happened while getting the last spotlight information.",
            })
          );
          navigate(ROUTE_DELETE_PRODUCT_PAGE);
          return;
        }
      } else {
        dispatch(setIsLoadingDataSpotlight(false));
      }
    }
  }, [dispatch, productId, rolesLoaded, needRefreshSpotlight]);

  useEffect(() => {
    loadSpotlightInfo();
  }, [loadSpotlightInfo]);

  return {
    actualProductName,
    lastSpotlight,
    spotlightHistory,
    isLoadingDataSpotlight,
    isSpotlightOn,
    isProductActive,
    isContentEditable,
    spotlightStatuses,
  };
};
